import { Suspense, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Sidebar } from '@/components/Sidebar';
import { MobileBottomAd } from '@/components/AdBanner';
import { SearchModal } from '@/components/search';
import { Bell, Settings, Search, Menu } from 'lucide-react';
import { IconButton, toast } from '@/components/ui';
import { PageLoadingSkeleton } from '@/components/PageLoadingSkeleton';
import { useDocumentRealtimeStatus } from '@/hooks';
import { useUIStore } from '@/stores';

interface AppLayoutProps {
  title?: string;
}

export function AppLayout({ title }: AppLayoutProps) {
  useDocumentRealtimeStatus();
  const navigate = useNavigate();
  const location = useLocation();
  const { setMobileMenuOpen, openSearch } = useUIStore();

  // Global Cmd+K / Ctrl+K shortcut to open search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        openSearch();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [openSearch]);

  // Dynamic page title
  useEffect(() => {
    const pageTitles: Record<string, string> = {
      '/dashboard': 'Dashboard',
      '/ask': 'Ask AI',
      '/reader': 'Reader',
      '/flashcards': 'Flashcards',
      '/quizzes': 'Quizzes',
      '/practice': 'Practice Tests',
      '/podcasts': 'Podcasts',
      '/lectures': 'Lectures',
      '/classes': 'Classes',
      '/progress': 'Progress',
      '/settings': 'Settings',
      '/pricing': 'Pricing',
      '/admin': 'Admin',
    };
    const basePath = '/' + location.pathname.split('/')[1];
    const pageTitle = pageTitles[basePath] || title || 'Dashboard';
    document.title = `${pageTitle} | Koda`;
  }, [location.pathname, title]);

  return (
    <div className="min-h-screen bg-background-primary">
      <Sidebar />
      
      <main className="md:ml-[260px] flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="sticky top-0 z-40 flex items-center justify-between px-4 md:px-6 py-4 bg-background-secondary border-b border-border-subtle">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 rounded-lg hover:bg-background-tertiary transition-colors md:hidden"
            >
              <Menu size={20} />
            </button>
            <div className="text-sm font-medium">
              {title || 'Dashboard'}
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Search */}
            <div
              onClick={openSearch}
              role="button"
              aria-label="Search knowledge base (Cmd+K)"
              className="hidden md:flex items-center gap-2 px-4 py-2 bg-background-tertiary border border-border-subtle hover:border-border-hover rounded-lg w-[300px] cursor-pointer transition-colors"
            >
              <Search size={16} className="text-foreground-muted" />
              <span className="flex-1 text-sm text-foreground-muted">
                Search your knowledge base...
              </span>
              <kbd className="hidden lg:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs text-foreground-muted bg-background-secondary border border-border-subtle rounded">
                <span>&#8984;</span><span>K</span>
              </kbd>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <IconButton icon={<Bell size={18} />} tooltip="Notifications" onClick={() => toast.info('Notifications coming soon!')} />
              <IconButton icon={<Settings size={18} />} tooltip="Settings" onClick={() => navigate('/settings')} />
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="flex-1 p-6 pb-20 lg:pb-6">
          <Suspense fallback={<PageLoadingSkeleton />}>
            <Outlet />
          </Suspense>
        </div>
      </main>

      {/* Mobile bottom ad - only shown for free users on mobile */}
      <MobileBottomAd />

      {/* Global search modal */}
      <SearchModal />
    </div>
  );
}
